/* Libraries */
import React, { memo, useMemo } from 'react';
import { Instance } from 'mobx-state-tree';

/* Components */
import Table from '~/src/containers/Table';
import { PdfCourtFormStore } from '~/src/stores/pdfCourtFormsStore';
import { ReactComponent as InformationIcon } from './information-icon.svg';
import { REQUEST_COURT_FORM_URL } from '~/src/constants/externalLinks';

const RequestCourtForm = () => (
  <div className="mt-2">
    <InformationIcon className="mr-2 mb-1" />
    <span className="mr-2 hidden lg:inline">
      Can&apos;t find the forms you are looking for?
    </span>
    <a href={REQUEST_COURT_FORM_URL} target="_blank" rel="noreferrer">
      Request a form
    </a>
  </div>
);

interface PdfCourtForm extends Instance<typeof PdfCourtFormStore> {}
interface Props {
  pdfCourtForms: PdfCourtForm[];
  visibleItems: number;
  empty: boolean;
  renderRowAction: (t: any) => any;
  onSearchChange: (value: string) => void;
  onSortByChange: (property: string, order: number) => void;
}

const PdfTable = ({
  pdfCourtForms,
  visibleItems,
  empty,
  renderRowAction,
  onSearchChange,
  onSortByChange,
}: Props) => {
  // Memoize columns array to prevent recreation on every render
  const columns = useMemo(
    () => [
      {
        label: 'Form number',
        dataKey: 'title',
        style: {
          width: '30%',
          textAlign: 'left',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
        },
      },
      {
        label: 'Form title',
        dataKey: 'subtitle',
        style: {
          width: '40%',
          textAlign: 'left',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
        },
      },
      {
        label: 'Actions',
        hideLabel: true,
        render: renderRowAction,
        style: { flexGrow: 1, display: 'flex', justifyContent: 'flex-end' },
      },
    ],
    [renderRowAction],
  );

  return (
    <div className="flex">
      <Table
        emptyTitle={'No Court Forms match that filter'}
        empty={empty}
        isLoading={false}
        searchPlaceholder="Search court forms"
        renderActions={RequestCourtForm}
        data={pdfCourtForms}
        visibleItems={visibleItems}
        onSearchChange={onSearchChange}
        isSortByDisabled={false}
        onSortByChange={onSortByChange}
        columns={columns}
      />
    </div>
  );
};

// Memoize the component with custom comparison
const MemoizedPdfTable = memo(PdfTable, (prevProps, nextProps) => {
  // Quick reference check first (most common case for stable references)
  if (
    prevProps.pdfCourtForms === nextProps.pdfCourtForms &&
    prevProps.visibleItems === nextProps.visibleItems &&
    prevProps.empty === nextProps.empty &&
    prevProps.renderRowAction === nextProps.renderRowAction &&
    prevProps.onSearchChange === nextProps.onSearchChange &&
    prevProps.onSortByChange === nextProps.onSortByChange
  ) {
    return true;
  }

  // Check if arrays are different references but same content
  if (prevProps.pdfCourtForms !== nextProps.pdfCourtForms) {
    if (prevProps.pdfCourtForms.length !== nextProps.pdfCourtForms.length) {
      return false;
    }

    // Shallow comparison of array items by ID
    const prevIds = prevProps.pdfCourtForms.map((item) => item.id).join(',');
    const nextIds = nextProps.pdfCourtForms.map((item) => item.id).join(',');

    if (prevIds !== nextIds) {
      return false;
    }
  }

  // Check other props
  if (
    prevProps.visibleItems !== nextProps.visibleItems ||
    prevProps.empty !== nextProps.empty
  ) {
    return false;
  }

  // Function references changed - this is the main cause of unnecessary re-renders
  if (
    prevProps.renderRowAction !== nextProps.renderRowAction ||
    prevProps.onSearchChange !== nextProps.onSearchChange ||
    prevProps.onSortByChange !== nextProps.onSortByChange
  ) {
    return false;
  }

  return true;
});

export default MemoizedPdfTable;
